import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Particles } from '../components/Particles';
import { authApi } from '../lib/api';

export function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');
      const type = searchParams.get('type'); // 'verify' ou 'reset'

      if (!token) {
        setStatus('error');
        setMessage('Token de verificação não encontrado.');
        return;
      }

      try {
        if (type === 'reset') {
          // Redirecionar para página de redefinição de senha
          navigate(`/reset-password?token=${token}`);
          return;
        }

        // Verificar email
        const response = await authApi.verifyEmail(token);

        // Salvar tokens e dados do usuário
        localStorage.setItem('access_token', response.access_token);
        localStorage.setItem('refresh_token', response.refresh_token);
        localStorage.setItem('user', JSON.stringify(response.user));

        setStatus('success');
        setMessage('Email verificado com sucesso! Redirecionando para a plataforma...');

        // Redirecionar para o athlete app (todos os usuários)
        setTimeout(() => {
          window.location.href = import.meta.env.VITE_ATHLETE_APP_URL || 'http://localhost:3002';
        }, 2000);
      } catch (error: any) {
        console.error('Erro na verificação:', error);

        setStatus('error');

        let errorMessage = 'Erro ao verificar email. Tente novamente.';

        if (error?.response?.status === 400) {
          errorMessage = error.response?.data?.detail || 'Token inválido ou expirado.';
        } else if (error?.response?.status === 404) {
          errorMessage = 'Token não encontrado ou já foi utilizado.';
        } else if (!error?.response) {
          errorMessage = 'Não foi possível conectar ao servidor. Verifique sua conexão.';
        }

        setMessage(errorMessage);
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  return (
    <>
      <Particles />

      <div className="auth-container">
        {/* Logo */}
        <div className="auth-logo">
          <img src="/images/logo/logo.png" alt="NeuroAtleta Mindset" />
          <h1>
            {status === 'loading' && 'Verificando seu email...'}
            {status === 'success' && 'Email Verificado!'}
            {status === 'error' && 'Erro na Verificação'}
          </h1>
        </div>

        {/* Status */}
        <div style={{ textAlign: 'center', padding: '2rem 0' }}>
          {status === 'loading' && (
            <>
              <div className="spinner" style={{ margin: '0 auto 1rem' }}></div>
              <p style={{ color: '#64748b' }}>Aguarde enquanto verificamos seu email...</p>
            </>
          )}

          {status === 'success' && (
            <div className="message success">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                style={{ display: 'inline-block', marginRight: '8px' }}
              >
                <path
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {message}
            </div>
          )}

          {status === 'error' && (
            <>
              <div className="message error">{message}</div>

              <div style={{ marginTop: '2rem' }}>
                <a href="/login" className="btn-primary" style={{ display: 'inline-block', marginRight: '1rem' }}>
                  Ir para Login
                </a>
                <a href="/register" className="btn-secondary" style={{ display: 'inline-block' }}>
                  Criar Nova Conta
                </a>
              </div>

              <div style={{ marginTop: '1.5rem' }}>
                <p style={{ color: '#64748b', fontSize: '14px' }}>
                  Se o link expirou, você pode solicitar um novo email de confirmação na página de login.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Voltar para home */}
        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <a href={import.meta.env.VITE_WEB_URL || 'http://localhost:3000'} className="back-link">
            ← Voltar para o site
          </a>
        </div>
      </div>
    </>
  );
}
